import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Checkbox, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetProductSalesReport } from '../../../hooks/reports/queries';
import { exportProductSalesReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import momentTz from 'moment-timezone';
import { getRangeDate } from '../datePickerHelper';

const ProductSalesReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const [groupBySection, setGroupBySection] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });

  const { fromDate, toDate } = getRangeDate(dateRangePicker);
  const timezone = momentTz.tz.guess(true);

  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetProductSalesReport(searchParamStoreId, {
    fromDate,
    toDate,
    timezone,
    groupBySection
  });

  const tableInfoData: any[] = useMemo(() => {
    return data?.tableInfo ?? [];
  }, [data]);
  const sectionsInfoData: any[] = useMemo(() => {
    return data?.sectionsInfo ?? [];
  }, [data]);
  const totalRowsCount = useMemo(() => {
    return groupBySection ? sectionsInfoData.length : tableInfoData.length;
  }, [groupBySection, sectionsInfoData, tableInfoData]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:productSalesReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            allowSingleDateInRange
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Checkbox
            checked={groupBySection}
            onChange={(event) => {
              setGroupBySection(event.currentTarget.checked);
              setPagination((current) => ({ ...current, pageIndex: 0 }));
            }}
            label={t('ReportsPage:productSalesReport.groupBySection')}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportProductSalesReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone,
                      groupBySection
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            storeId={searchParamStoreId}
            fromDate={fromDate}
            toDate={toDate}
            timezone={timezone}
            tableInfoData={tableInfoData}
            sectionsInfoData={sectionsInfoData}
            footerInfo={data?.ordersInfo || {}}
            groupBySection={groupBySection}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default ProductSalesReport;
